import React, { useEffect, useState } from 'react';
import { db } from '../db/firebase.js';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { useCronContext } from './CronContext.js';

const FIRESTORE_RULES = `rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {

    // Public read for frontend data
    match /nfts/{id}         { allow read: if true; }
    match /floor/{id}        { allow read: if true; }
    match /stats/{id}        { allow read: if true; }

    // Pre-computed public data written by cron jobs
    match /leaderboard/{id}  { allow read: if true; }
    match /mythics/{id}      { allow read: if true; }
    match /meta/{id}         { allow read: if true; }

    // Admin-only config (replace with your admin email)
    match /config/{id} {
      allow read, write: if request.auth != null
        && request.auth.token.email == "YOUR_ADMIN_EMAIL";
    }
  }
}`;

const statusMeta = {
    success: { label: 'Success', className: 'admin-badge-green' },
    failure: { label: 'Failed', className: 'admin-badge-red' },
    in_progress: { label: 'Running', className: 'admin-badge-yellow' },
    queued: { label: 'Queued', className: 'admin-badge-yellow' },
    cancelled: { label: 'Cancelled', className: 'admin-badge-gray' },
};

const timeAgo = (iso) => {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso);
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const StatCard = ({ label, value, sub, badge }) => (
    <div className="admin-stat-card">
        <div className="admin-stat-value">{value ?? '—'}</div>
        <div className="admin-stat-label">{label}</div>
        {badge && <span className={`admin-badge ${badge.className}`} style={{ marginTop: 6 }}>{badge.label}</span>}
        {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
);

const AdminOverview = () => {
    const { runs, loadingRuns, isRunning } = useCronContext();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rulesCopied, setRulesCopied] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [nftsSnap, configSnap, floorSnap] = await Promise.all([
                    getDocs(collection(db, 'nfts')),
                    getDoc(doc(db, 'config', 'nftIds')),
                    getDocs(query(collection(db, 'floor'), orderBy('__name__', 'desc'), limit(1))),
                ]);

                const nftDocs = nftsSnap.docs.map(d => d.data());
                const totalNfts = nftDocs.length;
                const uniqueRanks = new Set(nftDocs.map(n => n.rank).filter(Boolean)).size;
                const uniqueOwners = new Set(nftDocs.map(n => n.owner).filter(Boolean)).size;
                const trackedIds = configSnap.exists() ? (configSnap.data().ids || []).length : 0;

                let lastFloorDate = null;
                if (!floorSnap.empty) {
                    const raw = floorSnap.docs[0].id;
                    lastFloorDate = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
                }

                setStats({ totalNfts, uniqueRanks, uniqueOwners, trackedIds, lastFloorDate });
            } catch (err) {
                console.error('AdminOverview load error:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleCopyRules = () => {
        navigator.clipboard.writeText(FIRESTORE_RULES).then(() => {
            setRulesCopied(true);
            setTimeout(() => setRulesCopied(false), 2500);
        });
    };

    const lastRun = runs[0];
    const lastRunMeta = lastRun
        ? (statusMeta[lastRun.conclusion || lastRun.status] || { label: lastRun.conclusion || lastRun.status, className: 'admin-badge-gray' })
        : null;

    if (loading) return <div className="admin-loading">Loading overview...</div>;

    return (
        <div className="admin-section">
            <h2 className="admin-section-title">Overview</h2>

            <div className="admin-stat-grid">
                <StatCard label="Tracked IDs (config)" value={stats.trackedIds} />
                <StatCard label="NFTs in Firestore" value={stats.totalNfts} />
                <StatCard label="Unique Mythics" value={stats.uniqueRanks} />
                <StatCard label="Unique Owners" value={stats.uniqueOwners} />
                <StatCard label="Last Floor Update" value={stats.lastFloorDate || 'Never'} />
                <StatCard
                    label="Last Cron Run"
                    value={loadingRuns ? '…' : (lastRun ? timeAgo(lastRun.run_started_at || lastRun.created_at) : 'Never')}
                    badge={lastRunMeta}
                    sub={isRunning ? 'Currently running' : undefined}
                />
            </div>

            <div className="admin-info-box">
                <strong>How data flows:</strong>
                <ol>
                    <li>Admin adds NFT IDs here → saved to <code>config/nftIds</code> in Firestore.</li>
                    <li>Cron runs every 4 hours → reads IDs from Firestore → fetches OpenSea data → writes to <code>nfts/</code>.</li>
                    <li>Stats cron computes minting history → writes to <code>stats/latest</code>.</li>
                    <li>Floor cron fetches listings → writes to <code>floor/YYYYMMDD</code>.</li>
                </ol>
            </div>

            <details className="admin-rules-box">
                <summary>
                    <span>🔒</span> Firestore Security Rules
                </summary>
                <div className="admin-rules-body">
                    <p className="admin-hint">
                        Paste these rules in <a className="admin-link" href="https://console.firebase.google.com/project/yellow-nfts/firestore/rules" target="_blank" rel="noopener noreferrer">Firebase Console → Firestore → Rules</a>. Replace <code>YOUR_ADMIN_EMAIL</code> with your email.
                    </p>
                    <pre className="admin-rules-pre">{FIRESTORE_RULES}</pre>
                    <button className="admin-btn admin-btn-ghost" onClick={handleCopyRules}>
                        {rulesCopied ? '✓ Copied!' : 'Copy rules'}
                    </button>
                </div>
            </details>
        </div>
    );
};

export default AdminOverview;
